import React, { useRef } from "react"
import Grid from "components/Grid/Grid"
import dayjs from "dayjs"

// cell renderers
import LastModifiedBy from "components/ag-grid-cell-renderers/LastModifiedBy/LastModifiedBy"
import DocumentIconCellRenderer from "components/ag-grid-cell-renderers/DocumentIconCellRenderer/DocumentIconCellRenderer"
import DocumentNameTypeRenderer from "components/ag-grid-cell-renderers/DocumentNameType/DocumentNameType"
import DocumentActions from "components/ag-grid-cell-renderers/DocumentActions/DocumentActions"

import "./DocumentsGrid.scss"

// apis
import { useGetStudyDocuments } from "api/hooks/studies/useDocumentsApi"

// context
import useAuthoringStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringStore"

// utils
import useAgGridResize from "utilities/useAgGridResize"

const DocumentsGrid = ({ gridApi, setGridApi, search }) => {
  const gridContainer = useRef(null)

  const authoringId = useAuthoringStore((state) => state.authoringId)
  const { data: documents } = useGetStudyDocuments(authoringId)

  const gridBreakpoints = [
    {
      maxWidth: 1120,
      hide: [],
      show: ["Registries", "Countries", "Languages", "Last modified by", "Annotations"],
    },
    {
      maxWidth: 600,
      hide: [],
      show: ["Last modified by"],
    },
    {
      maxWidth: 400,
      hide: ["Last modified by", "Annotations"],
      show: [],
    },
  ]

  useAgGridResize(gridContainer, gridApi, gridBreakpoints)

  const gridOptions = {
    columnDefs: [
      {
        colId: "file",
        headerValueGetter: ({ location }) => {
          switch (location) {
            case "columnToolPanel":
              return "File/Link"
            default: // case "header"
              return ""
          }
        },
        cellRenderer: DocumentIconCellRenderer,
        valueGetter: ({ data }) => {
          // TODO: add more file types
          if (data?.document_file) {
            if (data.document_file.toLowerCase().includes(".pdf")) return "PDF"
            if (data.document_file.toLowerCase().includes(".xml")) return "XML"
            if (data.document_file.toLowerCase().includes(".csv")) return "CSV"
            if (data.document_file.toLowerCase().includes(".json")) return "JSON"
            if (data.document_file.toLowerCase().includes(".txt")) return "TEXT"
            if (data.document_file.toLowerCase().includes(".xslx")) return "XSLX"
            if (data.document_file.toLowerCase().includes(".xsl")) return "XSL"
            if (data.document_file.toLowerCase().includes(".docx")) return "DOCX"
            if (data.document_file.toLowerCase().includes(".doc")) return "DOC"
            return "other file"
          } else if (data?.document_link) {
            return "URL"
          }
        },
        resizable: false,
        width: 54,
      },
      {
        headerName: "Name/Type",
        field: "name",
        cellRenderer: DocumentNameTypeRenderer,
        valueGetter: ({ data }) => {
          return `${data?.name} - ${data?.document_type_name}`
        },
        minWidth: 250,
      },
      {
        headerName: "Last modified by",
        field: "last_modified",
        cellRenderer: LastModifiedBy,
        valueGetter: ({ data }) => {
          let date = null
          if (data?.last_modified) date = dayjs(data.last_modified).format("YYYY-MM-DD")
          console.log("last_modified", { data, last_modified: data?.last_modified, date })
          return date
        },
        width: 130,
      },
      {
        headerName: "",
        field: "",
        filter: false,
        cellRenderer: DocumentActions,
        cellRendererParams: {
          className: "doc-grid-actions-col",
          iconsClassName: "doc-grid-actions-col-icon",
        },
        suppressSizeToFit: true,
        suppressColumnsToolPanel: true,
        width: 100,
      },
    ],
    defaultColDef: {
      wrapText: true,
      filter: true,
      enableCharts: false,
      filterParams: {
        buttons: ["clear"],
      },
      cellStyle: { whiteSpace: "normal !important" },
      autoHeight: true,
      cellClassRules: {
        oddBackground: function (params) {
          return params.rowIndex % 2 === 0
        },
      },
    },
    columnTypes: {
      date: {
        filter: "agDateColumnFilter",
        filterParams: {
          resetButton: true,
          comparator: function (filterLocalDateAtMidnight, cellValue) {
            let cellDate = null
            let dateAsString = ""
            dateAsString = cellValue
            if (dateAsString === null) {
              return 0
            }
            cellDate = dayjs(dateAsString).toDate()
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
        },
      },
    },
    suppressHorizontalScroll: true,
    getRowHeight: () => 62,
    onColumnVisible: (params) => {
      setTimeout(() => {
        params.api.sizeColumnsToFit()
      }, 200)
    },
    onColumnPinned: (params) => {
      setTimeout(() => {
        params.api.sizeColumnsToFit()
      }, 200)
    },
    onColumnMoved: (params) => {
      setTimeout(() => {
        params.api.sizeColumnsToFit()
      }, 200)
    },
    rowClass: "doc-grid-row",
  }

  const onGridReady = (params) => {
    setGridApi(params.api)
    params.api.sizeColumnsToFit()
  }

  return (
    <div
      ref={gridContainer}
      className="ag-theme-quartz docs-grid"
    >
      <Grid
        onGridReady={onGridReady}
        gridOptions={gridOptions}
        rowData={documents}
        gridApi={gridApi}
        search={search}
        className="document-grid"
      />
    </div>
  )
}

export default DocumentsGrid
